import React from "react";
import {
  X,
  MonitorCog,
  WalletCards,
  Plus,
  CheckCircle2,
  CircleSlash2,
  RefreshCcw,
} from "lucide-react";
import LoadingScreen from "../../../../../utils/LoadingScreen";
import { formatDateTime } from "../../../../../utils/dateFormatter";

const TerminalDetailsModal = ({
  terminal,
  drawers,
  drawerLoading,
  onClose,
  onAddDrawer,
  onRefresh,
}) => {
  return (
    <>
    
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm montserrat-font-1">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden">
        {/* HEADER */}
        <div className="flex justify-between items-start px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <MonitorCog size={21} className="text-green-600" />
              {terminal.terminal_name || "Terminal Details"}
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Terminal details and attached cash drawers.
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full border hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-145px)]">
          {/* TERMINAL DETAILS */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div className="border rounded-xl p-4 bg-gray-50">
              <p className="text-xs text-gray-500">Terminal ID</p>
              <p className="font-semibold text-gray-800 mt-1">
                #{terminal.pk_terminal_id}
              </p>
            </div>

            <div className="border rounded-xl p-4 bg-gray-50">
              <p className="text-xs text-gray-500">Terminal Code</p>
              <p className="font-semibold text-gray-800 mt-1">
                {terminal.terminal_code || "-"}
              </p>
            </div>

            <div className="border rounded-xl p-4 bg-gray-50">
              <p className="text-xs text-gray-500">Device ID</p>
              <p className="font-semibold text-gray-800 mt-1">
                {terminal.device_id || "-"}
              </p>
            </div>

            <div className="border rounded-xl p-4 bg-gray-50">
              <p className="text-xs text-gray-500">Status</p>

              {String(terminal.status) === "1" ? (
                <p className="font-semibold text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle2 size={15} />
                  Active
                </p>
              ) : (
                <p className="font-semibold text-red-600 mt-1 flex items-center gap-1">
                  <CircleSlash2 size={15} />
                  Inactive
                </p>
              )}
            </div>
          </div>

          <div className="border rounded-xl p-4 mb-6">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <p>
                <span className="font-semibold text-gray-700">
                  Restaurant ID:
                </span>{" "}
                {terminal.fk_restaurant_id || "-"}
              </p>

              <p>
                <span className="font-semibold text-gray-700">
                  Created At:
                </span>{" "}
                {formatDateTime(terminal.created_at, true)}
              </p>
            </div>
          </div>

          {/* DRAWER HEADER */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <WalletCards size={18} className="text-blue-600" />
                Attached Drawers
              </h3>

              <p className="text-xs text-gray-500">
                Drawers mapped with terminal ID #{terminal.pk_terminal_id}.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onRefresh}
                className="border px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 flex gap-2 items-center text-sm"
              >
                <RefreshCcw size={15} />
                Refresh
              </button>

              <button
                onClick={onAddDrawer}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex gap-2 items-center text-sm"
              >
                <Plus size={15} />
                Add New Drawer
              </button>
            </div>
          </div>

          {/* DRAWER TABLE */}
          <div className="border rounded-xl overflow-hidden">
            {drawerLoading ? (
              <div className="py-10 flex justify-center">
                <LoadingScreen text="Loading drawers..." />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-blue-50 text-gray-600 text-xs uppercase">
                  <tr>
                    <th className="p-4 text-left">Drawer ID</th>
                    <th className="p-4 text-left">Drawer Name</th>
                    <th className="p-4 text-left">Drawer Code</th>
                    <th className="p-4 text-left">Status</th>
                    <th className="p-4 text-left">Created At</th>
                  </tr>
                </thead>

                <tbody>
                  {drawers.length === 0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        className="text-center py-8 text-gray-400"
                      >
                        No drawer attached to this terminal
                      </td>
                    </tr>
                  ) : (
                    drawers.map((drawer) => (
                      <tr
                        key={drawer.pk_drawer_id}
                        className="border-t hover:bg-gray-50"
                      >
                        <td className="p-4 font-mono text-gray-700">
                          #{drawer.pk_drawer_id}
                        </td>

                        <td className="p-4 font-medium text-gray-800">
                          {drawer.drawer_name || "-"}
                        </td>

                        <td className="p-4">
                          <span className="px-2 py-1 bg-gray-100 rounded-md font-mono text-xs">
                            {drawer.drawer_code || "-"}
                          </span>
                        </td>

                        <td className="p-4">
                          {String(drawer.is_active) === "1" ? (
                            <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-green-100 text-green-700 w-fit">
                              <CheckCircle2 size={14} />
                              Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-red-100 text-red-600 w-fit">
                              <CircleSlash2 size={14} />
                              Inactive
                            </span>
                          )}
                        </td>

                        <td className="p-4 text-gray-600">
                          {formatDateTime(drawer.created_at, true)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-end px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2 rounded-lg text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default TerminalDetailsModal;